/**
 * PubNub Legacy Bridge.
 *
 * Listens for `pubnub:<pnmType>` CustomEvents dispatched by
 * azure-web-pubsub-service.ts and calls the corresponding legacy global
 * functions. This replaces the message handler that was previously in
 * App.initPubNub() in app.ts.
 */

import { App } from '@/services/app-compat';

// ── Helper to listen for pubnub events ────────────────────────────

function on(pnmType: string, handler: (detail: any) => void): void {
  window.addEventListener(`pubnub:${pnmType}`, ((e: CustomEvent) => {
    handler(e.detail);
  }) as EventListener);
}

// ── Officer assigned channel ──────────────────────────────────────

on('officer:officerOnCallCard', (data) => {
  (window as any).showOfficerAssignedDialogAndNotify(
    new (window as any).OfficerAssignedDialogInfo(data)
  );
  (window as any).addMDTActiveCallCardFromPubNub(data.callCard, true);
});

on('officer:officerRemovedFromCallCard', (data) => {
  (window as any).removeMDTActiveCallCard(data.callCard);
});

on('officer:officerStayingOnCall', () => {
  (window as any).notifyOfficerStayingOnCallCard();
});

// ── User read-only channel ────────────────────────────────────────

on('user:caseReportSubmittedForSupervisors', (data) => {
  (window as any).showCaseReportSubmittedDialog(
    new (window as any).CaseReportSubmittedDialogInfo(data)
  );
});

on('user:startDispatcherPanic', (data) => {
  if (!(window as any).isPanicButtonGloballyEnabled()) return;
  (window as any).showPanicDialogAndNotify(
    new (window as any).PanicDialogInfo(data)
  );
  if (App.isViewingCADPage()) {
    (window as any).setCADPanicButtonBlinkState(data.callCard, true);
  }
});

on('user:endDispatcherPanic', (data) => {
  if (!App.isViewingCADPage() || !(window as any).isPanicButtonGloballyEnabled()) return;
  (window as any).setCADPanicButtonBlinkState(data.callCard, false);
});

on('user:startOfficerPanic', (data) => {
  if (!App.isViewingMDTPage() || !(window as any).isPanicButtonGloballyEnabled()) return;
  (window as any).setMDTPanicButtonBlinkState(data.callCard, true);
});

on('user:endOfficerPanic', (data) => {
  if (!App.isViewingMDTPage() || !(window as any).isPanicButtonGloballyEnabled()) return;
  (window as any).setMDTPanicButtonBlinkState(data.callCard, false);
});

// ── Company read-only channel ─────────────────────────────────────

on('company:callCardUpdate', (data) => {
  (window as any).realtimeUpdateCallCard(data.callCardId);
});

on('company:companyNotification', (data) => {
  (window as any).showCompanyNotification(data);
});

on('company:callCommentSave', (data) => {
  (window as any).addRealtimeCallComment(data);
});

on('company:callCommentUpdate', (data) => {
  (window as any).updateRealtimeCallComment(data);
});

on('company:guardTourStopComplete', (data) => {
  if (typeof (window as any).realtimeCompleteGuardTourStop === 'function') {
    (window as any).realtimeCompleteGuardTourStop(data.guardTourStopId, data.tourStopId);
  }
});

on('company:tourStopUpdate', (data) => {
  if (typeof (window as any).realtimeUpdateTourStop === 'function') {
    (window as any).realtimeUpdateTourStop(data.oldTourStopId, data.newTourStopId);
  }
});

on('company:companyRemovedFromCallCard', () => {
  (window as any).OrnaReact.navigate.toInProgress('MDT');
});

on('company:mapOverlayUpdate', () => {
  (window as any).realtimeUpdateMapOverlay();
});

on('company:customLocationUpdate', () => {
  (window as any).realtimeCustomLocationUpdate();
});

on('company:advisoryChanged', () => {
  // Notify the Advisory operational screen (cross-component CustomEvent pattern)
  // so it can reload immediately instead of waiting for the safety poll.
  window.dispatchEvent(new CustomEvent('orna:advisory-changed'));
});

// ── Company read-write channel ────────────────────────────────────

on('companyRW:companyChatMessage', (data) => {
  (window as any).CompanyChat?.receiveCompanyChatMessage(data, data._timetoken);
});

// ── Orna read-only channel ────────────────────────────────────────

on('orna:ornaVersionUpdate', () => {
  App.showVersionUpdateDialog();
});

// ── Presence ──────────────────────────────────────────────────────

window.addEventListener('pubnub:presence', ((e: CustomEvent) => {
  const chat = (window as any).CompanyChat;
  if (chat?.handlePresenceEvent) {
    chat.handlePresenceEvent(e.detail);
  }
}) as EventListener);
